import React, { useState } from 'react';
import { ICourse, IDogAttachment, DOG_VACCINE_TYPES } from "@eagerdog/interfaces";

import ContextMenu from "src/components/ContextMenu/ContextMenu";
import { Modal, useModal } from "src/components/Modal/Modal";
import Button from "src/components/Button/Button";
import Checkbox from "src/components/Checkbox/Checkbox";
import Input from "src/components/Input/Input";
import { toast } from "src/components/Toast/ToastManager";

import { apiService } from "src/services/api.service";

import type { CustomCellRendererProps } from 'ag-grid-react';

import styles from "./CourseRegistrations.module.scss";

const POINTS_PER_DOLLAR = 1000;
const MAX_CREDIT_DOLLARS = 500;

interface CourseRegistrationActionsRendererProps extends CustomCellRendererProps {
  course: ICourse,
  onRemoved(registrationId: string): void
}

export const ActionsRenderer = (props: CourseRegistrationActionsRendererProps) => {
  const { course } = props;
  const { isShown, toggle } = useModal();
  const [isRemoving, setIsRemoving] = useState<boolean>(false);
  const [creditEnabled, setCreditEnabled] = useState<boolean>(false);

  const vaccineModal = useModal();
  const [vaccineAttachments, setVaccineAttachments] = useState<IDogAttachment[]>([]);
  const [vaccineLoading, setVaccineLoading] = useState<boolean>(false);
  const [vaccineError, setVaccineError] = useState<boolean>(false);

  const registration = props.data?.attendance?.registration;
  const sessionLabel: string = props.data?.session || "this session";
  const callName: string = props.data?.call_name || "";

  // Prorated session value: per-session override, else course fee / session count.
  const sessionFee = (() => {
    const sessions = course?.schedule || [];
    const session = sessions.find((s: any) => s._id === registration?.session_id);
    if (session && typeof session.fee === "number") {
      return session.fee;
    }
    return sessions.length > 0 ? course.fee / sessions.length : 0;
  })();

  const [creditDollars, setCreditDollars] = useState<string>(sessionFee.toFixed(2));

  const dollarsNum = parseFloat(creditDollars) || 0;
  const creditPoints = Math.round(dollarsNum * POINTS_PER_DOLLAR);
  const overCap = dollarsNum > MAX_CREDIT_DOLLARS;
  const creditInvalid = creditEnabled && (dollarsNum <= 0 || overCap);

  const closeModal = () => {
    setCreditEnabled(false);
    setCreditDollars(sessionFee.toFixed(2));
    toggle();
  };

  const openVaccineModal = async () => {
    const dogId = registration?.dog_id;
    if (!dogId) {
      toast.show({ title: "Vaccines", content: "No dog linked to this registration.", duration: 4000, type: "fail" });
      return;
    }
    setVaccineAttachments([]);
    setVaccineError(false);
    setVaccineLoading(true);
    vaccineModal.toggle();
    try {
      const dog = await apiService.getDog(dogId);
      setVaccineAttachments((dog.attachments ?? []) as IDogAttachment[]);
    } catch {
      setVaccineError(true);
    } finally {
      setVaccineLoading(false);
    }
  };

  const removeDog = () => {
    if (!registration?._id || creditInvalid) {
      return;
    }

    setIsRemoving(true);

    const pointsToCredit = creditEnabled ? creditPoints : undefined;

    apiService.deleteCourseSessionRegistration(course._id, registration._id, pointsToCredit)
      .then((result) => {
        if (creditEnabled && !result.credit_applied) {
          toast.show({
            title: "Remove Dog",
            content: `${callName} was removed from ${sessionLabel}, but we couldn't find the registrant to apply the credit.`,
            duration: 10000,
            type: "fail"
          });
        } else {
          toast.show({
            title: "Remove Dog",
            content: creditEnabled
              ? `${callName} has been removed from ${sessionLabel} and credited ${creditPoints.toLocaleString()} points ($${dollarsNum.toFixed(2)}).`
              : `${callName} has been removed from ${sessionLabel}.`,
            duration: 10000,
            type: "success"
          });
        }

        toggle();
        props.onRemoved(registration._id);
      })
      .catch((error: any) => {
        toast.show({
          title: "Remove Dog",
          content: error.response?.data?.message ?? "Something went wrong, please try again later",
          duration: 10000,
          type: "fail"
        });
      })
      .finally(() => {
        setIsRemoving(false);
      });
  };

  return (<div className={styles.ActionRenderer}>
    <ContextMenu
      options={[
        {
          label: "View Vaccine(s)",
          onClick: openVaccineModal
        },
        {
          label: "Remove Dog",
          onClick: () => { toggle(); }
        }
      ]}
    />
    <Modal
      className={"vaccineModal"}
      isShown={vaccineModal.isShown}
      hide={vaccineModal.toggle}
      modalContent={
          <div className={styles.vaccineModal}>
            <div className={styles.vaccineModalHeader}>
              <div className={styles.vaccineModalTitle}>Vaccine Records</div>
              <div className={styles.vaccineModalSubtitle}>{callName}</div>
            </div>
            {vaccineLoading ? (
              <p className={styles.vaccineModalEmpty}>Loading…</p>
            ) : vaccineError ? (
              <p className={styles.vaccineModalEmpty}>Failed to load vaccine records. Please try again.</p>
            ) : vaccineAttachments.length === 0 ? (
              <p className={styles.vaccineModalEmpty}>No vaccine certificates on file for this dog.</p>
            ) : (
              <div className={styles.vaccineList}>
                {vaccineAttachments.map((att, i) => {
                  const typeLabel = DOG_VACCINE_TYPES.find(t => t.value === att.type)?.label ?? att.type;
                  const expired = att.expiry_date && new Date(att.expiry_date) < new Date();
                  return (
                    <div key={att._id ?? i} className={`${styles.vaccineItem}${expired ? ` ${styles.vaccineItemExpired}` : ""}`}>
                      <div className={styles.vaccineItemInfo}>
                        <span className={styles.vaccineItemType}>{typeLabel}</span>
                        <span className={`${styles.vaccineItemExpiry}${expired ? ` ${styles.vaccineItemExpiryBad}` : ""}`}>
                          Expires: {new Date(att.expiry_date).toLocaleDateString()}{expired ? " — Expired" : ""}
                        </span>
                      </div>
                      <a
                        className={styles.vaccineItemLink}
                        href={att.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View ↗
                      </a>
                    </div>
                  );
                })}
              </div>
            )}
            <div className={styles.vaccineModalActions}>
              <Button type="button" onClick={vaccineModal.toggle}>Close</Button>
            </div>
          </div>
      }
    />
    <Modal
      className={"confirmModal"}
      isShown={isShown}
      hide={closeModal}
      modalContent={
        <div className={styles.removeModal}>
          <div className={styles.removeTitle}>Remove Dog</div>
          <p className={styles.removeText}>
            {callName ? <b>{callName}</b> : "This entry"} will be removed from <b>{sessionLabel}</b>.
            <br />
            This does not issue a refund.
          </p>

          <div className={styles.creditSection}>
            <Checkbox
              id="provideCredit"
              value={creditEnabled}
              onChange={() => { setCreditEnabled(!creditEnabled); }}
              label="Would you like to provide a credit for this session?"
            />

            {creditEnabled && (
              <div className={styles.creditFields}>
                <p className={styles.creditHelper}>
                  Session value is prorated from the course fee. Adjust the amount if needed.
                </p>
                <Input
                  type="number"
                  min="0.01"
                  max={MAX_CREDIT_DOLLARS}
                  step="0.01"
                  label="Credit Amount ($)"
                  value={creditDollars}
                  onChange={(e: any) => { setCreditDollars(e.target.value); }}
                />
                <div className={styles.pointsLine}>
                  Points awarded: <b>{creditPoints.toLocaleString()}</b>
                  <span className={styles.pointsHint}> (1,000 points = $1)</span>
                </div>
                {overCap && (
                  <div className={styles.creditError}>
                    Maximum credit is ${MAX_CREDIT_DOLLARS.toFixed(2)} per session.
                  </div>
                )}
              </div>
            )}
          </div>

          <div className={styles.removeActions}>
            <Button className="cancel" type="button" disabled={isRemoving} onClick={closeModal}>
              Cancel
            </Button>
            <Button type="button" isLoading={isRemoving} disabled={isRemoving || creditInvalid} onClick={removeDog}>
              {creditEnabled ? "Remove & Credit" : "Remove Dog"}
            </Button>
          </div>
        </div>
      }
    />
  </div>);
};
